/**
 * Version Management Service
 *
 * Handles version CRUD operations for all schedule types.
 * Currently uses mock data; will be wired to actual API endpoints.
 */

import type { Version, VersionDiff, EntityType } from '../types/versioned-entity.types'
import { generateVersionDescription } from '../utils/version-description'

// Helper to create a standard set of mock versions for any entity
function createMockVersions(
  v3Note: string,
  v3Changes: Version['changes'],
  v2Note: string,
  v2Changes: Version['changes'],
): Version[] {
  return [
    {
      id: 'v3',
      version: 3,
      status: 'active',
      createdBy: 'user-123',
      createdByName: 'John Doe',
      createdAt: '2026-02-15T10:00:00Z',
      note: v3Note,
      description: generateVersionDescription(v3Changes),
      effectiveDate: '2026-03-01',
      changes: v3Changes,
    },
    {
      id: 'v2',
      version: 2,
      status: 'archived',
      createdBy: 'user-456',
      createdByName: 'Jane Smith',
      createdAt: '2026-01-15T10:00:00Z',
      note: v2Note,
      description: generateVersionDescription(v2Changes),
      changes: v2Changes,
    },
    {
      id: 'v1',
      version: 1,
      status: 'archived',
      createdBy: 'user-123',
      createdByName: 'John Doe',
      createdAt: '2025-12-01T10:00:00Z',
      note: 'Initial configuration',
      description: 'Initial version',
      changes: [],
    },
  ]
}

// Mock version data for development
const MOCK_VERSIONS: Record<string, Version[]> = {
  // Tariff schedules
  'tariff-1': createMockVersions(
    'Updated contract renewal terms',
    [{ field: 'contractPeriod.renewalType', fieldLabel: 'Renewal Type', oldValue: 'manual', newValue: 'automatic', changeType: 'modified' }],
    'Added new phase for Q2',
    [{ field: 'phases', fieldLabel: 'Phases', oldValue: 1, newValue: 2, changeType: 'added' }],
  ),
  // Fee schedules
  'fee-FS-001': createMockVersions(
    'Adjusted blended fee rate',
    [{ field: 'blendedRate', fieldLabel: 'Blended Rate', oldValue: '2.5%', newValue: '2.9%', changeType: 'modified' }],
    'Added volume tier pricing',
    [{ field: 'volumeTiers', fieldLabel: 'Volume Tiers', oldValue: null, newValue: '3 tiers', changeType: 'added' }],
  ),
  'fee-FS-002': createMockVersions(
    'Updated fixed fee amount',
    [{ field: 'fixedAmount', fieldLabel: 'Fixed Amount', oldValue: '$0.25', newValue: '$0.30', changeType: 'modified' }],
    'Changed fee type to variable',
    [{ field: 'feeType', fieldLabel: 'Fee Type', oldValue: 'fixed', newValue: 'variable', changeType: 'modified' }],
  ),
  // Payout schedules
  'payout-ps-001': createMockVersions(
    'Changed payout frequency to daily',
    [{ field: 'frequency', fieldLabel: 'Frequency', oldValue: 'weekly', newValue: 'daily', changeType: 'modified' }],
    'Updated payout model',
    [{ field: 'model', fieldLabel: 'Payout Model', oldValue: 'pass-through-payout', newValue: 'same-day-payout', changeType: 'modified' }],
  ),
  'payout-ps-002': createMockVersions(
    'Updated currency to EUR',
    [{ field: 'currency', fieldLabel: 'Currency', oldValue: 'USD', newValue: 'EUR', changeType: 'modified' }],
    'Added description',
    [{ field: 'description', fieldLabel: 'Description', oldValue: null, newValue: 'EU merchant payouts', changeType: 'added' }],
  ),
  // FX schedules
  'fx-FXS-001': createMockVersions(
    'Updated default markup',
    [{ field: 'defaultMarkup', fieldLabel: 'Default Markup', oldValue: '1.5%', newValue: '2.0%', changeType: 'modified' }],
    'Added GBP/EUR currency pair',
    [{ field: 'currencyPairs', fieldLabel: 'Currency Pairs', oldValue: null, newValue: 'GBP/EUR', changeType: 'added' }],
  ),
  'fx-FXS-002': createMockVersions(
    'Changed rate source to manual',
    [{ field: 'rateSource', fieldLabel: 'Rate Source', oldValue: 'provider', newValue: 'manual', changeType: 'modified' }],
    'Updated base currency',
    [{ field: 'baseCurrency', fieldLabel: 'Base Currency', oldValue: 'USD', newValue: 'GBP', changeType: 'modified' }],
  ),
  // Velocity schedules
  'velocity-VL-001': createMockVersions(
    'Updated threshold action to block',
    [{ field: 'thresholds[0].action', fieldLabel: 'Threshold Action', oldValue: 'review', newValue: 'block', changeType: 'modified' }],
    'Added new velocity rule condition',
    [{ field: 'rules[1]', fieldLabel: 'Rule #2', oldValue: null, newValue: 'card_country = US', changeType: 'added' }],
  ),
  // Fraud schedules
  'fraud-schedule-FRS-001': createMockVersions(
    'Enabled 3DS authentication rule',
    [{ field: 'rules[2].status', fieldLabel: 'Rule Status', oldValue: 'Disabled', newValue: 'Enabled', changeType: 'modified' }],
    'Added decline rule for high-risk BINs',
    [{ field: 'rules[3]', fieldLabel: 'Decline Rule', oldValue: null, newValue: 'BIN in high-risk list', changeType: 'added' }],
  ),
  'fraud-schedule-FRS-002': createMockVersions(
    'Updated block rule threshold',
    [{ field: 'rules[0].condition', fieldLabel: 'Block Condition', oldValue: 'amount > 5000', newValue: 'amount > 3000', changeType: 'modified' }],
    'Added review rule',
    [{ field: 'rules[1]', fieldLabel: 'Review Rule', oldValue: null, newValue: 'country != US', changeType: 'added' }],
  ),
  // Billing schedules
  'billing-schedule-1': createMockVersions(
    'Updated payment terms to Net 30',
    [{ field: 'paymentTerms', fieldLabel: 'Payment Terms', oldValue: 'Net 15', newValue: 'Net 30', changeType: 'modified' }],
    'Added Phase 2 billing period',
    [{ field: 'phases', fieldLabel: 'Phases', oldValue: 1, newValue: 2, changeType: 'added' }],
  ),
}

export class VersionService {
  /**
   * Fetch all versions for an entity
   */
  async getVersions(entityType: EntityType, entityId: string): Promise<Version[]> {
    await new Promise(resolve => setTimeout(resolve, 300))

    const key = `${entityType}-${entityId}`
    return MOCK_VERSIONS[key] || this.generateFallbackVersions()
  }

  /**
   * Fetch a specific version
   */
  async getVersion(
    entityType: EntityType,
    entityId: string,
    versionId: string
  ): Promise<Version | null> {
    const versions = await this.getVersions(entityType, entityId)
    return versions.find(v => v.id === versionId) || null
  }

  /**
   * Create a new version
   */
  async createVersion(
    entityType: EntityType,
    entityId: string,
    _data: unknown,
    note?: string
  ): Promise<Version> {
    await new Promise(resolve => setTimeout(resolve, 500))

    const versions = await this.getVersions(entityType, entityId)
    const latestVersion = versions[0]?.version || 0

    const newVersion: Version = {
      id: `v${latestVersion + 1}`,
      version: latestVersion + 1,
      status: 'active',
      createdBy: 'current-user',
      createdByName: 'Current User',
      createdAt: new Date().toISOString(),
      note: note || '',
      description: note || 'New version',
      changes: [],
    }

    return newVersion
  }

  /**
   * Compare two versions and return differences
   */
  async compareVersions(
    entityType: EntityType,
    entityId: string,
    v1Id: string,
    v2Id: string
  ): Promise<VersionDiff> {
    const [v1, v2] = await Promise.all([
      this.getVersion(entityType, entityId, v1Id),
      this.getVersion(entityType, entityId, v2Id),
    ])

    if (!v1 || !v2) {
      throw new Error('Version not found')
    }

    const changes = v2.changes

    return { v1, v2, changes }
  }

  /**
   * Restore a previous version (creates new version with old data)
   */
  async restoreVersion(
    entityType: EntityType,
    entityId: string,
    versionId: string
  ): Promise<Version> {
    await new Promise(resolve => setTimeout(resolve, 500))

    const versionToRestore = await this.getVersion(entityType, entityId, versionId)
    if (!versionToRestore) {
      throw new Error('Version not found')
    }

    return this.createVersion(
      entityType,
      entityId,
      {},
      `Restored from version ${versionToRestore.version}`
    )
  }

  /**
   * Archive a version
   */
  async archiveVersion(
    _entityType: EntityType,
    _entityId: string,
    _versionId: string
  ): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300))
  }

  /**
   * Generate fallback versions for entities without explicit mock data
   */
  private generateFallbackVersions(): Version[] {
    return [
      {
        id: 'v2',
        version: 2,
        status: 'active',
        createdBy: 'user-123',
        createdByName: 'John Doe',
        createdAt: '2026-02-10T10:00:00Z',
        note: 'Configuration update',
        description: 'Updated configuration',
        changes: [
          { field: 'config', fieldLabel: 'Configuration', oldValue: 'v1', newValue: 'v2', changeType: 'modified' },
        ],
      },
      {
        id: 'v1',
        version: 1,
        status: 'archived',
        createdBy: 'user-123',
        createdByName: 'John Doe',
        createdAt: '2025-12-01T10:00:00Z',
        note: 'Initial configuration',
        description: 'Initial version',
        changes: [],
      },
    ]
  }
}

export const versionService = new VersionService()
